import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Textarea, Field, Stat, StatusBadge } from "@/components/UI";
import { Plus, Trash, Coins, Trophy, ChartLine, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PerformerField } from "@/components/VetTable";
import { today } from "./helpers";


export function InseminationEditModal({ item, bulls, user, onClose, onDone }) {
  const [form, setForm] = useState({
    animal_type: item.animal_type || "inek",
    animal_count: item.animal_count || 1,
    date: (item.date || today()).slice(0, 10),
    bull_id: item.bull_id || "",
    bull_code: item.bull_code || "",
    method: item.method || "suni",
    semen_type: item.semen_type || "konvansiyonel",
    technician: item.technician || "",
    notes: item.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const oldBullId = item.bull_id || null;
  const oldUsed = Number(item.payets_used || 0);

  const onBullChange = (bid) => {
    if (!bid) { setForm(f => ({ ...f, bull_id: "", bull_code: "" })); return; }
    const b = bulls.find(x => x.id === bid);
    setForm(f => ({ ...f, bull_id: bid, bull_code: b?.bull_code || "", semen_type: b?.semen_type || f.semen_type }));
  };

  const selectedBull = bulls.find(x => x.id === form.bull_id);
  const willConsume = Number(form.animal_count || 0);
  // Aynı boğaysa bu kayıtta halihazırda kullanılmış payetler de kullanılabilir sayılır
  const available = selectedBull
    ? (selectedBull.payet_count || 0) + (form.bull_id === oldBullId ? oldUsed : 0)
    : Infinity;
  const notEnough = selectedBull && willConsume > available;

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.animal_count) < 1) return toast.error("En az 1 hayvan girin");
    if (notEnough) return toast.error(`'${selectedBull.bull_name}' boğasında yeterli payet yok. Kullanılabilir: ${available}`);
    setSaving(true);
    try {
      await api.put(`/vet/inseminations/${item.id}`, {
        ...form,
        technician: form.technician?.trim() || user?.name || "",
        animal_count: Number(form.animal_count),
        bull_id: form.bull_id || null,
        bull_code: form.bull_code || null,
      });
      toast.success("Tohumlama kaydı güncellendi");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Hata");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="ins-edit-modal">
      <div className="bg-white rounded-md max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="heading text-xl">Tohumlama Kaydını Düzenle</h3>
          <button onClick={onClose} data-testid="ins-edit-close" className="text-2xl text-[#8a938c] hover:text-[#1A1D1A]">×</button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hayvan Tipi *">
              <Select data-testid="ins-edit-animal-type" value={form.animal_type} onChange={e => setForm({ ...form, animal_type: e.target.value })}>
                <option value="inek">İnek</option>
                <option value="düve">Düve</option>
              </Select>
            </Field>
            <Field label="Adet *">
              <Input data-testid="ins-edit-count" type="number" min="1" required value={form.animal_count} onChange={e => setForm({ ...form, animal_count: e.target.value })} />
            </Field>
          </div>
          <Field label="Tarih *"><Input type="date" data-testid="ins-edit-date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Kullanılan Boğa (Sperma Stoğundan)">
            <Select data-testid="ins-edit-bull-select" value={form.bull_id} onChange={e => onBullChange(e.target.value)}>
              <option value="">— Boğa seçin (opsiyonel) —</option>
              {bulls.map(b => (
                <option key={b.id} value={b.id}>
                  {b.bull_name} {b.bull_code ? `(${b.bull_code})` : ""} · {b.payet_count} payet · {b.semen_type}
                </option>
              ))}
            </Select>
          </Field>
          {selectedBull && (
            <div className={`text-xs px-3 py-2 rounded ${notEnough ? "bg-[#FBECEA] border border-[#E9B5AB] text-[#8B2E1D]" : "bg-[#E4EEE6] border border-[#c2d6c8] text-[#2D4A36]"}`} data-testid="ins-edit-bull-preview">
              {notEnough ? "⚠️ " : "✓ "}
              <b>{selectedBull.bull_name}</b> · Kullanılabilir {available} payet
              {willConsume > 0 && <> · Bu kayıt için {willConsume}</>}
              {form.bull_id === oldBullId && oldUsed > 0 && (
                <div className="mt-0.5 text-[#5e675e]">Bu kayıtta önceden {oldUsed} payet kullanılmış — düzenlemede iade edilip yeniden hesaplanır.</div>
              )}
              {notEnough && <div className="mt-1">Yeterli payet yok. Önce stok girişi yapın.</div>}
            </div>
          )}
          <Field label="Tohum Tipi *">
            <Select data-testid="ins-edit-semen" value={form.semen_type} onChange={e => setForm({ ...form, semen_type: e.target.value })}>
              <option value="konvansiyonel">Konvansiyonel</option>
              <option value="dişi">Dişi (Cinsiyet ayırt.)</option>
              <option value="erkek">Erkek (Cinsiyet ayırt.)</option>
            </Select>
          </Field>
          <Field label="Yöntem">
            <Select value={form.method} onChange={e => setForm({ ...form, method: e.target.value })}>
              <option>suni</option><option>doğal</option>
            </Select>
          </Field>
          <Field label="Boğa Kodu (elle giriş)"><Input data-testid="ins-edit-bull-code" value={form.bull_code} onChange={e => setForm({ ...form, bull_code: e.target.value })} /></Field>
          <PerformerField label="Tohumlayan (İşlemi Yapan)" user={user} value={form.technician}
            onChange={v => setForm({ ...form, technician: v })} testId="ins-edit-tech" />
          <Field label="Notlar"><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={onClose}>İptal</Button>
            <Button data-testid="ins-edit-submit" type="submit" disabled={notEnough || saving}>{saving ? "Kaydediliyor…" : "Güncelle"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

